import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography
} from '@mui/material';
import { LinkOff as LinkOffIcon } from '@mui/icons-material';
import { connectQuickBooks, disconnectQuickBooks, getQuickBooksStatus } from './api';
import connectButton from './assets/C2QB_green_btn_med_default.svg';
import connectButtonHover from './assets/C2QB_green_btn_med_hover.svg';

const RESULT_MESSAGES = {
  connected: { severity: 'success', message: 'QuickBooks is connected.' },
  cancelled: { severity: 'info', message: 'You cancelled connecting QuickBooks. Nothing changed.' },
  expired: { severity: 'warning', message: 'That QuickBooks sign-in took too long or was already used. Please connect again.' },
  error: { severity: 'error', message: 'QuickBooks couldn’t be connected. Please try again.' }
};

const DISCONNECTED_MESSAGE = {
  severity: 'info',
  message: 'QuickBooks is disconnected. ZenRentFlow won’t send anything to QuickBooks until you connect again.'
};

const DISCONNECTED_UNCONFIRMED_MESSAGE = {
  severity: 'warning',
  message:
    'QuickBooks is disconnected here, but QuickBooks didn’t confirm it. If ZenRentFlow still shows under Apps in QuickBooks, you can remove it there.'
};

const FINISH_DISCONNECT_MESSAGE = {
  severity: 'info',
  message: 'Coming from QuickBooks? To finish disconnecting, confirm below.'
};

function errorMessage(err, fallback) {
  return err?.response?.data?.error || fallback;
}

/** Intuit requires its own button graphics, unaltered and at their original size, until a company is connected. */
function ConnectToQuickBooksButton({ onClick, busy }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <ButtonBase
        onClick={onClick}
        disabled={busy}
        aria-label="Connect to QuickBooks"
        disableRipple
        sx={{
          borderRadius: '4px',
          '& .c2qb-hover': { display: 'none' },
          '&:hover .c2qb-default, &.Mui-focusVisible .c2qb-default': { display: 'none' },
          '&:hover .c2qb-hover, &.Mui-focusVisible .c2qb-hover': { display: 'block' },
          '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 }
        }}
      >
        <img className="c2qb-default" src={connectButton} width={223} height={36} alt="" />
        <img className="c2qb-hover" src={connectButtonHover} width={223} height={36} alt="" />
      </ButtonBase>
      {busy && (
        <>
          <CircularProgress size={18} aria-hidden="true" />
          <Typography variant="body2" color="text.secondary" role="status">
            Opening QuickBooks…
          </Typography>
        </>
      )}
    </Box>
  );
}

/**
 * Connect / reconnect / disconnect QuickBooks Online. Renders nothing while QuickBooks isn't set up on the server.
 * `link` (from the /quickbooks addresses registered with Intuit) is acted on once; `onLinkHandled`
 * lets the parent drop it so coming back to this page doesn't repeat it.
 * @param {{
 *   link?: { action: 'connect'|'disconnected'|null, result: string|null } | null,
 *   onLinkHandled?: () => void,
 *   onStatus?: (status: object) => void,
 *   goTo?: (url: string) => void
 * }} props
 */
function QuickBooksConnection({ link = null, onLinkHandled, onStatus, goTo = (url) => window.location.assign(url) }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(() => (link?.result ? RESULT_MESSAGES[link.result] : null));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const handledLink = useRef(false);
  const onStatusRef = useRef(onStatus);
  useEffect(() => {
    onStatusRef.current = onStatus;
  });

  const apply = useCallback((next) => {
    setStatus(next);
    if (onStatusRef.current) onStatusRef.current(next);
  }, []);

  const startConnect = useCallback(async () => {
    setBusy(true);
    setNotice(null);
    try {
      const { authUrl } = await connectQuickBooks();
      goTo(authUrl);
    } catch (err) {
      setBusy(false);
      setNotice({ severity: 'error', message: errorMessage(err, 'Could not start connecting QuickBooks.') });
    }
  }, [goTo]);

  const disconnect = useCallback(async () => {
    setBusy(true);
    try {
      const result = await disconnectQuickBooks();
      apply({ ...status, connected: false, status: 'not_connected', companyName: null });
      setNotice(result?.revoked === false ? DISCONNECTED_UNCONFIRMED_MESSAGE : DISCONNECTED_MESSAGE);
    } catch (err) {
      setNotice({ severity: 'error', message: errorMessage(err, 'Could not disconnect QuickBooks. Please try again.') });
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  }, [apply, status]);

  useEffect(() => {
    let cancelled = false;
    getQuickBooksStatus()
      .then((data) => {
        if (!cancelled) apply(data);
      })
      .catch(() => {
        if (!cancelled) apply({ configured: false, connected: false, status: 'not_connected' });
      });
    return () => {
      cancelled = true;
    };
  }, [apply]);

  useEffect(() => {
    if (!status || !link || handledLink.current) return;
    handledLink.current = true;
    if (onLinkHandled) onLinkHandled();
    if (!status.configured) return;
    if (link.action === 'connect' && !status.connected) {
      startConnect();
    } else if (link.action === 'disconnected') {
      if (status.status === 'not_connected') {
        setNotice(DISCONNECTED_MESSAGE);
      } else {
        setNotice(FINISH_DISCONNECT_MESSAGE);
        setConfirmOpen(true);
      }
    }
  }, [status, link, onLinkHandled, startConnect]);

  if (!status) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} aria-label="Checking QuickBooks" />
      </Box>
    );
  }
  if (!status.configured) return null;

  const needsReauth = status.status === 'needs_reauth';

  return (
    <Box
      component="section"
      aria-labelledby="qb-connection-heading"
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mb: 3 }}
    >
      {notice && (
        <Alert severity={notice.severity} sx={{ mb: 1.5 }} onClose={() => setNotice(null)}>
          {notice.message}
        </Alert>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ flex: '1 1 260px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography id="qb-connection-heading" variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
              {status.connected ? `Connected to ${status.companyName || 'QuickBooks'}` : 'Connect QuickBooks'}
            </Typography>
            {status.environment === 'sandbox' && <Chip size="small" variant="outlined" label="Sandbox" />}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {status.connected
              ? 'You choose when to send each month. Nothing goes to QuickBooks until you click Send.'
              : needsReauth
                ? 'QuickBooks asked you to sign in again before ZenRentFlow can send anything.'
                : 'Connect your QuickBooks Online company to send each month’s entry straight to QuickBooks.'}
          </Typography>
        </Box>
        {status.connected ? (
          <Button variant="outlined" color="inherit" startIcon={<LinkOffIcon />} onClick={() => setConfirmOpen(true)} disabled={busy}>
            Disconnect from QuickBooks
          </Button>
        ) : (
          <ConnectToQuickBooksButton onClick={startConnect} busy={busy} />
        )}
      </Box>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} aria-labelledby="qb-disconnect-title">
        <DialogTitle id="qb-disconnect-title">Disconnect QuickBooks?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`ZenRentFlow will stop sending to ${status.companyName || 'your QuickBooks company'}. Entries already sent stay in QuickBooks.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Keep connected</Button>
          <Button color="error" onClick={disconnect} disabled={busy}>
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default QuickBooksConnection;
